import { useState, useEffect, useRef } from 'react';
import CameraFeed from './CameraFeed';
import HeatmapOverlay from './HeatmapOverlay';
import ModernDashboard from './components/ModernDashboard';
import HistoryPanel from './HistoryPanel';
import SignalGraph from './SignalGraph';
import { printReport } from './utils/export';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge-2";
import { Moon, Sun, Camera, Activity, AlertCircle } from 'lucide-react';
import './App.css';

/**
 * Main Application Component
 * 
 * Orchestrates webcam capture, WebSocket communication, and real-time display.
 * This is a biomedical engineering prototype, not a diagnostic tool.
 */
function App() {
  // WebSocket connection
  const [ws, setWs] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');

  // Vital signs data
  const [vitals, setVitals] = useState({
    heartRate: null,
    hrvRmssd: null,
    breathingRate: null,
    signalQuality: 0,
    status: 'idle'
  });

  // Vitals History for Sparklines (Last 30 points)
  const [vitalsHistory, setVitalsHistory] = useState({
    heart_rate: [],
    hrv_rmssd: [],
    breathing_rate: []
  });

  // Video frame reference
  const [currentFrame, setCurrentFrame] = useState(null);
  const [landmarks, setLandmarks] = useState(null);
  const [currentSignals, setCurrentSignals] = useState(null);

  // Ref to access latest vitals inside stale callbacks (like the camera loop)
  const vitalsRef = useRef(vitals);
  useEffect(() => {
    vitalsRef.current = vitals;
  }, [vitals]);

  // Buffer progress for user feedback
  const [bufferProgress, setBufferProgress] = useState(0);

  // Camera state
  const [cameraActive, setCameraActive] = useState(false);
  const [scanningComplete, setScanningComplete] = useState(false);
  const [scanPhase, setScanPhase] = useState('idle'); // idle, detecting, scanning, measuring, complete

  // Scanning animation state
  const [scanProgress, setScanProgress] = useState(0);
  const scanProgressRef = useRef(0);
  useEffect(() => {
    scanProgressRef.current = scanProgress;
  }, [scanProgress]);

  // Measurement history (max 10)
  const [measurementHistory, setMeasurementHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('scan'); // 'scan' or 'history'

  // Best Frame Capture
  const [bestFrame, setBestFrame] = useState(null);
  const maxSignalQualityRef = useRef(0);
  const lastFrameRef = useRef(null); // Fallback for missing best frame

  // Theme support
  const [theme, setTheme] = useState('dark');

  useEffect(() => {
    document.documentElement.className = theme;
    // Also toggle body class for legacy css support if needed
    document.body.className = theme === 'light' ? 'light-mode' : '';
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

  // Timer for measurement completion
  const measurementStartTimeRef = useRef(null);

  // Connection retry logic
  const reconnectTimeoutRef = useRef(null);
  const maxReconnectAttempts = 5;
  const reconnectAttemptRef = useRef(0);

  /**
   * Initialize WebSocket connection
   */
  const connectWebSocket = () => {
    // Check for environment variable (Cloudflare Pages), otherwise default to localhost
    const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:8000/ws/vitals';

    try {
      const socket = new WebSocket(wsUrl);

      socket.onopen = () => {
        console.log('WebSocket connected');
        setConnectionStatus('connected');
        reconnectAttemptRef.current = 0;
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleServerMessage(data);
        } catch (error) {
          console.error('Failed to parse server message:', error);
        }
      };

      socket.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('error');
      };

      socket.onclose = () => {
        console.log('WebSocket disconnected');
        setConnectionStatus('disconnected');

        // Attempt reconnection with exponential backoff
        if (reconnectAttemptRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptRef.current), 10000);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptRef.current += 1;
            connectWebSocket();
          }, delay);
        }
      };

      setWs(socket);
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      setConnectionStatus('error');
    }
  };

  /**
   * Handle messages from backend
   */
  const handleServerMessage = (data) => {
    if (data.status === 'success' && data.vitals) {
      setVitals({
        heartRate: data.vitals.heart_rate,
        hrvRmssd: data.vitals.hrv_rmssd,
        breathingRate: data.vitals.breathing_rate,
        signalQuality: data.vitals.signal_quality,
        status: 'success',
        activeAlgorithm: data.active_algorithm
      });
      // Update landmarks if provided
      if (data.landmarks) {
        setLandmarks(data.landmarks);
      }

      // Update History for Sparklines
      setVitalsHistory(prev => {
        const updateList = (list, newVal) => {
          if (newVal === null) return list;
          const newList = [...list, newVal];
          if (newList.length > 50) return newList.slice(newList.length - 50);
          return newList;
        };
        return {
          heart_rate: updateList(prev.heart_rate, data.vitals.heart_rate),
          hrv_rmssd: updateList(prev.hrv_rmssd, data.vitals.hrv_rmssd),
          breathing_rate: updateList(prev.breathing_rate, data.vitals.breathing_rate),
        };
      });

      // Store session stats if available
      if (data.vitals && data.vitals.session_averages) {
        setVitals(prev => ({
          ...prev,
          sessionMedianHR: data.vitals.session_averages.heart_rate,
          sessionMedianHRV: data.vitals.session_averages.hrv_rmssd,
          sessionStats: data.vitals.session_averages // Store full object
        }));
      }

      // Update Signals for Inspector
      if (data.signals) {
        setCurrentSignals(data.signals);
      }


      // Update scan phase
      if (vitals.heartRate === null && data.vitals.heart_rate !== null) {
        // First successful measurement - start timer
        setScanPhase('measuring');
        if (!measurementStartTimeRef.current) {
          measurementStartTimeRef.current = Date.now();
        }
      }

      // Auto-complete logic - TIME-BASED
      if (data.vitals.heart_rate !== null) {
        if (!measurementStartTimeRef.current) {
          measurementStartTimeRef.current = Date.now();
          setScanPhase('measuring');
        }

        const elapsed = Date.now() - measurementStartTimeRef.current;

        // Stop after 25 seconds of measurement (Total ~30s) or 60s total
        const totalElapsed = Date.now() - (window.scanStartTime || Date.now());

        if ((elapsed > 25000 || totalElapsed > 60000) && !scanningComplete) {
          console.log('Auto-stopping scan:', { elapsed, totalElapsed, quality: data.vitals.signal_quality });

          // Save measurement
          saveMeasurement({
            heartRate: data.vitals.heart_rate, // Live backup
            sessionMedianHR: data.vitals.session_averages?.heart_rate, // Robust Session Stat from Backend
            hrvRmssd: data.vitals.session_averages?.hrv_rmssd || data.vitals.hrv_rmssd,
            breathingRate: data.vitals.session_averages?.breathing_rate || data.vitals.breathing_rate,
            signalQuality: data.vitals.signal_quality
          });

          // Stop scan
          setScanningComplete(true);
          setScanPhase('complete');
          setCameraActive(false); // IMPORTANT: Stop camera
          measurementStartTimeRef.current = null;
        }
      }

    } else if (data.status === 'accumulating') {
      // Only update buffer progress if we are NOT yet measuring
      if (scanPhase !== 'measuring' && !scanningComplete) {
        const progress = data.buffer_progress || 0;
        setBufferProgress(progress);
        setScanProgress(progress);
      } else {
        setBufferProgress(0);
      }
      setVitals(prev => ({ ...prev, status: 'accumulating' }));
      setScanPhase('scanning');
      // Update landmarks even during accumulation
      if (data.landmarks) {
        setLandmarks(data.landmarks);
      }
    } else if (data.status === 'no_face') {
      setVitals(prev => ({ ...prev, status: 'no_face' }));
      setLandmarks(null); // Clear landmarks when no face
      setScanPhase('detecting');
    } else if (data.status === 'insufficient_data') {
      const progress = data.buffer_progress || 0;
      setBufferProgress(progress);
      setScanProgress(progress);
      setVitals(prev => ({ ...prev, status: 'insufficient_data' }));
      setScanPhase('scanning');
      if (data.landmarks) {
        setLandmarks(data.landmarks);
      }
    } else if (data.status === 'error') {
      console.error('Server error:', data.message);
      setVitals(prev => ({ ...prev, status: 'error' }));
      setLandmarks(null);
      setScanPhase('idle');
    }
  };

  /**
   * Send frame to backend via WebSocket
   */
  const sendFrame = (frameDataUrl) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      // Extract base64 data (remove "data:image/jpeg;base64," prefix)
      const base64Data = frameDataUrl.split(',')[1];

      const message = {
        frame: base64Data
      };

      // Update last frame ref for fallback
      lastFrameRef.current = frameDataUrl;

      ws.send(JSON.stringify(message));

      // Best Frame Capture Logic
      // Target the "Middle Phase" (30% to 70%) for the most representative capture
      const currentQuality = vitalsRef.current.signalQuality;
      const progress = scanProgressRef.current;

      const isMiddlePhase = progress > 0.3 && progress < 0.7;

      // If we are in the middle phase, we aggressively look for the best frame
      if (isMiddlePhase && scanningComplete === false) {
        if (currentQuality > maxSignalQualityRef.current && currentQuality > 0.6) {
          maxSignalQualityRef.current = currentQuality;
          setBestFrame(frameDataUrl);
        }
      }
      // Fallback: If we assume we haven't found a good frame yet (quality is low), we continue searching
      else if (!bestFrame && currentQuality > 0.7 && !scanningComplete) {
        setBestFrame(frameDataUrl);
        maxSignalQualityRef.current = currentQuality;
      }
    }
  };

  /**
   * Save completed measurement to history
   */
  const saveMeasurement = (vitals) => {
    // USE FALLBACK: If bestFrame is null, use the last captured frame
    const finalImage = bestFrame || lastFrameRef.current;

    if (!finalImage) {
      console.warn("No image available for history");
    }

    const measurement = {
      timestamp: new Date().toISOString(),
      heartRate: vitals.sessionMedianHR || vitals.heartRate, // Prefer robust median
      hrvRmssd: vitals.hrvRmssd,
      breathingRate: vitals.breathingRate,
      signalQuality: vitals.signalQuality,
      image: finalImage // Best or Last
    };

    setMeasurementHistory(prev => {
      // Safe Storage Logic
      const safelyStore = (items) => {
        try {
          localStorage.setItem('vitalScanHistory', JSON.stringify(items));
        } catch (error) {
          if (error.name === 'QuotaExceededError' || error.storageArea) {
            console.warn("LocalStorage Quota Exceeded. Removing oldest item...");
            // Remove oldest and try again (recursive)
            if (items.length > 1) {
              safelyStore(items.slice(0, -1));
            } else {
              // Extreme case: Can't even store one. Remove image.
              console.error("Cannot store even one record. Stripping image.");
              const noImage = { ...items[0], image: null };
              try { localStorage.setItem('vitalScanHistory', JSON.stringify([noImage])); } catch (e) { }
            }
          }
        }
      };

      const updated = [measurement, ...prev];
      const limited = updated.slice(0, 10); // Keep only last 10
      safelyStore(limited);

      return limited;
    });
  };

  /**
   * Clear measurement history
   */
  const clearHistory = () => {
    if (window.confirm('Are you sure you want to clear all measurement history?')) {
      setMeasurementHistory([]);
      try {
        localStorage.removeItem('vitalScanHistory');
      } catch (error) {
        console.error('Failed to clear localStorage:', error);
      }
    }
  };

  /**
   * Load measurement history from localStorage on mount
   */
  useEffect(() => {
    try {
      const stored = localStorage.getItem('vitalScanHistory');
      if (stored) {
        const history = JSON.parse(stored);
        setMeasurementHistory(history);
      }
    } catch (error) {
      console.error('Failed to load measurement history:', error);
    }
  }, []);

  /**
   * Initialize WebSocket on mount
   */
  useEffect(() => {
    connectWebSocket();

    return () => {
      if (ws) {
        ws.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Toggle camera
   */
  const toggleCamera = () => {
    if (cameraActive || scanningComplete) {
      // Stop and reset
      setCameraActive(false);
      setScanningComplete(false);
      setScanPhase('idle');
      setScanProgress(0);
      setVitals({
        heartRate: null,
        hrvRmssd: null,
        breathingRate: null,
        signalQuality: 0,
        status: 'idle'
      });
      setVitalsHistory({ heart_rate: [], hrv_rmssd: [], breathing_rate: [] });
      setBufferProgress(0);
      setLandmarks(null);
      setBestFrame(null);
      maxSignalQualityRef.current = 0;
    } else {
      // Start scan
      setCameraActive(true);
      setScanningComplete(false);
      setScanPhase('detecting');
      window.scanStartTime = Date.now(); // Reset total time tracker
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col font-sans selection:bg-primary/20 transition-colors duration-300">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b bg-background/80 backdrop-blur-md">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-primary/10 p-2 rounded-lg">
              <Activity className="h-6 w-6 text-primary" />
            </div>
            <div className="flex flex-col">
              <h1 className="text-xl font-bold tracking-tight">Vital Scan AI</h1>
              <span className="text-xs text-muted-foreground uppercase tracking-widest font-mono hidden sm:block">rPPG Biomarker Analysis</span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Badge variant={connectionStatus === 'connected' ? 'success' : connectionStatus === 'error' ? 'destructive' : 'warning'} className="gap-1.5 hidden sm:inline-flex">
              <div className={`h-1.5 w-1.5 rounded-full ${connectionStatus === 'connected' ? 'bg-white' : 'bg-white/80'}`} />
              {connectionStatus === 'connected' ? 'System Online' : connectionStatus === 'error' ? 'Error' : 'Connecting'}
            </Badge>

            <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-full">
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Main content area */}
      <main className="flex-1 container mx-auto px-4 py-8 flex flex-col gap-8">
        {/* Navigation Tabs (Simulated) */}
        <div className="flex items-center gap-2 border-b pb-px overflow-x-auto">
          <Button
            variant={activeTab === 'scan' ? 'secondary' : 'ghost'}
            onClick={() => setActiveTab('scan')}
            className="rounded-b-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-muted/50"
            data-state={activeTab === 'scan' ? 'active' : ''}
          >
            <Camera className="mr-2 h-4 w-4" /> Live Scan
          </Button>
          <Button
            variant={activeTab === 'history' ? 'secondary' : 'ghost'}
            onClick={() => setActiveTab('history')}
            className="rounded-b-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-muted/50"
            data-state={activeTab === 'history' ? 'active' : ''}
          >
            <Activity className="mr-2 h-4 w-4" /> History
            {measurementHistory.length > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 min-w-5 px-1">{measurementHistory.length}</Badge>
            )}
          </Button>
        </div>

        {/* Scan Tab Content */}
        {activeTab === 'scan' && (
          <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">

            {/* Camera Section */}
            <div className="space-y-6">
              {/* Camera Card */}
              <Card className="overflow-hidden border-2 border-muted hover:border-border transition-colors">
                <div className="relative aspect-video bg-muted/30">
                  <CameraFeed
                    active={cameraActive}
                    onFrame={sendFrame}
                    onFrameUpdate={setCurrentFrame}
                  />
                  {cameraActive && currentFrame && (
                    <HeatmapOverlay
                      frame={currentFrame}
                      signalQuality={vitals.signalQuality}
                      landmarks={landmarks}
                      scanPhase={scanPhase}
                      scanProgress={scanProgress}
                      heartRate={vitals.heartRate}
                    />
                  )}

                  {!cameraActive && !scanningComplete && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground gap-4 bg-muted/10 backdrop-blur-[2px]">
                      <div className="bg-background/80 p-6 rounded-full shadow-xl">
                        <Camera className="h-12 w-12 opacity-50" />
                      </div>
                      <p className="font-mono text-sm">Click &quot;Start Scan&quot; to begin analysis</p>
                    </div>
                  )}

                  {/* Status Overlay */}
                  {(cameraActive || scanningComplete) && (
                    <div className="absolute top-4 right-4">
                      <Badge variant={scanningComplete ? 'success' : cameraActive ? 'primary' : 'secondary'} className="uppercase tracking-wider shadow-lg backdrop-blur-md">
                        {scanningComplete ? "Scan Complete" :
                          scanPhase === 'measuring' ? "Measuring..." :
                            vitals.status === 'accumulating' ? "Calibrating..." :
                              cameraActive ? "Detecting Face..." : "Ready"}
                      </Badge>
                    </div>
                  )}
                </div>
              </Card>

              {/* Controls Bar */}
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between p-4 bg-muted/30 rounded-xl border border-border/50">
                <div className="w-full sm:w-auto flex items-center gap-4">
                  <Button
                    size="lg"
                    variant={cameraActive ? "destructive" : scanningComplete ? "outline" : "primary"}
                    onClick={toggleCamera}
                    disabled={connectionStatus !== 'connected'}
                    className="w-full sm:w-40 font-mono tracking-wider shadow-lg shadow-primary/10"
                  >
                    {scanningComplete ? 'New Scan' : (cameraActive ? 'Stop Scan' : 'Start Scan')}
                  </Button>

                  {cameraActive && !scanningComplete && vitals.status === 'accumulating' && bufferProgress > 0 && scanPhase !== 'measuring' && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground font-mono">
                      <div className="h-2 w-24 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary transition-all duration-300" style={{ width: `${bufferProgress * 100}%` }} />
                      </div>
                      <span>{Math.round(bufferProgress * 100)}%</span>
                    </div>
                  )}
                </div>

                {scanningComplete && vitals.heartRate && (
                  <Button
                    variant="secondary"
                    onClick={() => printReport({
                      heartRate: Math.round(vitals.heartRate),
                      hrvRmssd: vitals.hrvRmssd,
                      breathingRate: vitals.breathingRate,
                      signalQuality: vitals.signalQuality,
                      timestamp: Date.now()
                    })}
                    className="w-full sm:w-auto"
                  >
                    Download Report
                  </Button>
                )}
              </div>
            </div>

            {/* Dashboard & Results Section */}
            <div className="space-y-6">

              {/* Best Frame Result */}
              {scanningComplete && bestFrame && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                  <Card className="overflow-hidden border-primary/50 shadow-lg shadow-primary/10">
                    <div className="p-3 border-b bg-muted/50">
                      <h3 className="text-sm font-medium">Best Capture</h3>
                    </div>
                    <div className="relative aspect-video bg-black">
                      <img src={bestFrame} alt="Best Frame" className="w-full h-full object-cover" />
                    </div>
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-muted-foreground font-mono">
                        Captured at max signal quality ({Math.round(maxSignalQualityRef.current * 100)}%)
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-muted/30 border-dashed h-full">
                    <CardContent className="p-6 flex flex-col justify-center h-full space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-full text-primary">
                          <Activity className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Scan Analysis</h3>
                          <p className="text-sm text-muted-foreground">High confidence measurement</p>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        The system successfully extracted physiological signals from the facial region.
                        The values below represent the median of the stable measurement window.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* This replaces VitalDashboard with Modern structure */}
              <ModernDashboard
                vitals={vitals}
                history={vitalsHistory}
                isScanning={cameraActive && !scanningComplete}
                sessionAverages={vitals.sessionStats}
              />

              {/* Tips Card */}
              <Card className="bg-muted/30 border-dashed">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-xs font-bold uppercase tracking-wider">Tips for Accuracy</span>
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-2 list-disc list-inside">
                    <li>Ensure good lighting on your face</li>
                    <li>Hold still during the 30s scan</li>
                    <li>Remove glasses if possible</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
            {/* Signal Graph (Bottom) */}
            {cameraActive && (
              <Card className="overflow-hidden border-t-0 shadow-inner">
                <div className="p-1 bg-muted/50 border-b flex justify-between items-center px-4 py-2">
                  <span className="text-xs font-mono text-muted-foreground uppercase">Real-time PPG Signal</span>
                  <div className="flex gap-2">
                    <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-[10px] text-red-500 font-mono uppercase">Live</span>
                  </div>
                </div>
                {/* Wrapped Signal Graph */}
                <div className="relative w-full h-48 bg-background">
                  <SignalGraph signals={currentSignals} height={192} />
                </div>
              </Card>
            )}

          </div>
        )}

        {/* History Tab Content */}
        {activeTab === 'history' && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-6">Measurement History</h2>
                <HistoryPanel
                  measurements={measurementHistory}
                  onClear={clearHistory}
                />
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t bg-muted/20 mt-auto">
        <div className="container mx-auto px-4 py-8 flex flex-col items-center gap-4 text-center">
          <div className="flex flex-wrap justify-center gap-8 text-xs font-mono text-muted-foreground">
            <span>Algorithm: <strong>POS</strong> (Plane-Orthogonal-to-Skin)</span>
            <span>Detection: <strong>MediaPipe FaceMesh</strong></span>
            <span>Sampling: <strong>30 FPS</strong> / 30s Window</span>
          </div>
          <p className="text-[10px] text-muted-foreground/60 max-w-lg">
            <strong>Research Prototype Only:</strong> This application is for demonstration purposes.
            Measurements are estimations and not medical diagnoses.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
